'use client'

// "New scene" on a story (flag story_new_scene): describe the next scene in words, or pick one of the session's
// pictures to animate as it. Only pictures of the story's shape (portrait, square, landscape) are offered: the joined
// video would have to crop or bar any other. Either way it's the usual Animate modal (ClipChainCard hands it to the
// feed's onAnimate with the story's link); describing is offered first, as most video makers have no pictures.
import { useState } from 'react'
import { FaPen } from 'react-icons/fa'

import Modal from '../Modal'

export type Picture = {
  id: string
  url: string
  isUrl?: boolean
  docRef?: string
}

type Props = {
  isOpen: boolean
  onClose: () => void
  pictures: Picture[]
  /** The story's width / height, once its video has loaded; until then every picture can be picked. */
  storyRatio: number | null
  onDescribe: () => void
  onPick: (picture: Picture) => void
  t: (key: string, options?: Record<string, unknown>) => string
}

/** Portrait, square or landscape: what must match for a scene to join its story without bars. */
export const shapeOf = (ratio: number) =>
  ratio < 0.9 ? 'portrait' : ratio > 1.1 ? 'landscape' : 'square'

const src = (p: Picture) =>
  p.isUrl ? p.url : `data:image/jpeg;base64,${p.url}`

export default function ScenePicker({
  isOpen,
  onClose,
  pictures,
  storyRatio,
  onDescribe,
  onPick,
  t,
}: Props) {
  // Each picture's shape, from its own pixels as its thumbnail loads.
  const [ratios, setRatios] = useState<Record<string, number>>({})
  // A picture shows once its shape is known, so one of another shape never appears and then vanishes.
  const fitting = pictures.filter(
    (p) =>
      ratios[p.id] &&
      (!storyRatio || shapeOf(ratios[p.id]) === shapeOf(storyRatio)),
  )

  const content = (
    <div>
      <p className="mb-3 text-sm text-gray-300">
        {t('clips_scene_sheet_hint')}
      </p>
      <button
        type="button"
        data-track="generate.clips_scene_describe"
        onClick={onDescribe}
        className="flex w-full items-center gap-3 rounded-2xl bg-[var(--main-bg-color)] px-4 py-3 text-left text-white transition-transform active:scale-[0.98]"
      >
        <FaPen className="shrink-0" />
        <span>
          <span className="block text-sm font-bold">
            {t('clips_scene_describe')}
          </span>
          <span className="block text-xs text-white/80">
            {t('clips_scene_describe_hint')}
          </span>
        </span>
      </button>
      {fitting.length > 0 && (
        <p className="mb-2 mt-4 text-xs font-semibold uppercase tracking-wide text-gray-400">
          {t('clips_scene_or_picture')}
        </p>
      )}
      <div className="grid max-h-[45vh] grid-cols-3 gap-2 overflow-y-auto">
        {pictures.map((p) => (
          <button
            key={p.id}
            type="button"
            data-track="generate.clips_scene_pick"
            onClick={() => onPick(p)}
            className={`relative overflow-hidden rounded-xl border-2 border-transparent transition-colors hover:border-[var(--main-bg-color)] ${
              fitting.includes(p) ? '' : 'hidden'
            }`}
          >
            <img
              src={src(p)}
              alt=""
              // Not lazy: a hidden picture must still load to learn its shape.
              onLoad={(e) => {
                const img = e.currentTarget
                if (img.naturalHeight)
                  setRatios((r) => ({
                    ...r,
                    [p.id]: img.naturalWidth / img.naturalHeight,
                  }))
              }}
              className="aspect-[3/4] w-full object-cover"
            />
          </button>
        ))}
      </div>
    </div>
  )

  return (
    <Modal
      isOpen={isOpen}
      closeModal={onClose}
      title={t('clips_new_scene')}
      content={content}
    />
  )
}
